import { useState, useMemo, useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { ChatMode, DsFileChange, UserChatEntry } from 'dragonmouse-shared/interfaces';
import { trackDesignSystemAction } from 'analytics';
import Modal from 'components/Modal';
import { useDsChatQueue } from 'hooks/useDsChatQueue';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsChat } from 'redux/db/reducers';
import forge from 'apis/forge';
import { toast } from 'react-toastify';
import { interceptIfNoCredits } from 'economy';
import * as S from './styles';

interface DraftChangesModalProps {
	mode: 'view' | 'publish' | 'discard';
	onClose: () => void;
}

const STATUS_ORDER: Record<DsFileChange['status'], number> = {
	added: 0,
	modified: 1,
	removed: 2,
};

const STATUS_LABEL: Record<DsFileChange['status'], string> = {
	added: 'Added',
	modified: 'Modified',
	removed: 'Removed',
};

const DraftChangesModal = ({ mode, onClose }: DraftChangesModalProps) => {
	const { draftChanges, activeDesignSystemId, dsTitle, dsVersionRefs, activeDsChat } = useSelector(
		(r: RootReducer) => ({
			draftChanges: r.db.draftChanges,
			activeDesignSystemId: r.mode.activeDesignSystemId,
			dsTitle: r.db.dsTitle,
			dsVersionRefs: r.db.dsVersionRefs,
			activeDsChat: selectActiveDsChat(r.db),
		}),
		shallowEqual
	);
	const [loading, setLoading] = useState(false);
	const [summaries, setSummaries] = useState<Record<string, string>>({});
	const [summarizing, setSummarizing] = useState<Record<string, boolean>>({});
	const [discarding, setDiscarding] = useState<Record<string, boolean>>({});

	const nextVersionNumber = dsVersionRefs.length > 0 ? dsVersionRefs[0].versionNumber + 1 : 1;

	const sorted = useMemo(() => {
		if (!draftChanges) return [];
		return [...draftChanges].sort(
			(a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.path.localeCompare(b.path)
		);
	}, [draftChanges]);

	const counts = useMemo(() => {
		const c = { added: 0, modified: 0, removed: 0 };
		for (const ch of sorted) c[ch.status]++;
		return c;
	}, [sorted]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onPublish = async () => {
		if (!activeDesignSystemId) return;
		setLoading(true);
		try {
			const response = await forge.publishDsVersion(activeDesignSystemId);
			trackDesignSystemAction('publish');
			toast.success(`Design system published as v${response.versionNumber}`);
			onClose();
		} catch {
			toast.error('Failed to publish design system');
			setLoading(false);
		}
	};

	const onDiscardAll = async () => {
		if (!activeDesignSystemId) return;
		setLoading(true);
		try {
			await forge.discardDsVersion(activeDesignSystemId);
			trackDesignSystemAction('revert');
			toast.success('Draft changes discarded');
			onClose();
		} catch {
			toast.error('Failed to discard changes');
			setLoading(false);
		}
	};

	const onDiscardFile = useCallback(
		async (change: DsFileChange) => {
			if (!activeDesignSystemId) return;
			setDiscarding((prev) => ({ ...prev, [change.path]: true }));
			try {
				let fileContent: string;
				if (change.status === 'added') {
					// Delete the added file by sending empty content
					fileContent = '';
				} else if (change.status === 'removed') {
					// Restore the removed file with its prior content
					fileContent = change.prior ?? '';
				} else {
					// Modified: restore to published content
					fileContent = change.prior ?? '';
				}
				await forge.updateDsFiles({
					designSystemId: activeDesignSystemId,
					files: [{ path: change.path, content: fileContent }],
				});
				toast.success(`Discarded changes to ${change.path}`);
			} catch {
				toast.error(`Failed to discard ${change.path}`);
			} finally {
				setDiscarding((prev) => ({ ...prev, [change.path]: false }));
			}
		},
		[activeDesignSystemId]
	);

	const onSummarizeFile = useCallback(
		async (change: DsFileChange) => {
			if (!activeDesignSystemId) return;
			if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: activeDesignSystemId })) return;
			setSummarizing((prev) => ({ ...prev, [change.path]: true }));
			try {
				const resp = await forge.summarizeFileChange.withToast()({
					designSystemId: activeDesignSystemId,
					path: change.path,
				});
				if (resp) {
					setSummaries((prev) => ({ ...prev, [change.path]: resp.summary }));
				}
			} catch {
				// withToast already handles the error display
			} finally {
				setSummarizing((prev) => ({ ...prev, [change.path]: false }));
			}
		},
		[activeDesignSystemId]
	);

	const queue = useDsChatQueue(activeDsChat?.id ?? null);

	const onSummarizeAll = useCallback(() => {
		if (!activeDesignSystemId) return;
		if (!activeDsChat) {
			toast.error('Open a design system chat first to summarize all changes.');
			return;
		}
		if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: activeDesignSystemId })) return;

		// Enqueue and close the modal. The queue lives in Redux so it survives
		// the modal unmount; the executor fires it when the chat goes idle.
		// QueueIndicator above the chat input shows the queued state and
		// supports cancel.
		const message =
			'Please summarize all changes in the current draft compared to the last published version. ' +
			'Give a high-level, thematic overview of what changed and why it matters — ' +
			'focus on design intent and impact rather than listing individual file diffs.';

		const entry: UserChatEntry = {
			kind: 'user',
			message,
			attachments: [],
			sourceFrameIds: [],
		};
		queue.enqueue({
			label: 'Summarize draft changes',
			payload: { kind: 'addUserEntry', entry, chatMode: ChatMode.CHAT_ONLY },
		});
		onClose();
	}, [activeDesignSystemId, activeDsChat, onClose, queue]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const title =
		mode === 'publish'
			? `Publish v${nextVersionNumber}?`
			: mode === 'discard'
			? `Discard changes to ${dsTitle}?`
			: 'Draft Changes';

	const submitLabel =
		mode === 'publish' ? 'Publish' : mode === 'discard' ? 'Discard Changes' : undefined;
	const onSubmit = mode === 'publish' ? onPublish : mode === 'discard' ? onDiscardAll : undefined;

	const renderRowActions = (ch: DsFileChange) => {
		const isSummarizing = summarizing[ch.path];
		const isDiscardingFile = discarding[ch.path];

		return (
			<S.RowActions>
				{summaries[ch.path] ? null : (
					<S.RowButton onClick={() => onSummarizeFile(ch)} disabled={isSummarizing}>
						{isSummarizing ? 'Summarizing…' : 'Summarize'}
					</S.RowButton>
				)}
				<S.RowButton onClick={() => onDiscardFile(ch)} disabled={isDiscardingFile}>
					{isDiscardingFile ? 'Discarding…' : 'Discard'}
				</S.RowButton>
			</S.RowActions>
		);
	};

	const renderSummary = () => {
		const parts: string[] = [];
		if (counts.added > 0) parts.push(`${counts.added} added`);
		if (counts.modified > 0) parts.push(`${counts.modified} modified`);
		if (counts.removed > 0) parts.push(`${counts.removed} removed`);
		if (parts.length === 0) return null;
		return <S.Summary>{parts.join(', ')}</S.Summary>;
	};

	const renderViewToolbar = () => {
		if (mode !== 'view' || sorted.length === 0) return null;
		return (
			<S.ViewToolbar>
				<S.ToolbarButton onClick={onSummarizeAll}>Summarize all</S.ToolbarButton>
				<S.ToolbarButton $danger onClick={onDiscardAll} disabled={loading}>
					{loading ? 'Discarding…' : 'Discard all changes'}
				</S.ToolbarButton>
			</S.ViewToolbar>
		);
	};

	const renderBody = () => {
		if (!draftChanges || sorted.length === 0) {
			return <S.EmptyState>No changes from the published version.</S.EmptyState>;
		}
		return (
			<>
				{renderSummary()}
				{renderViewToolbar()}
				<S.List>
					{sorted.map((ch) => (
						<S.FileEntry key={ch.path}>
							<S.FileRow>
								<S.FilePath>{ch.path}</S.FilePath>
								<S.StatusBadge $status={ch.status}>{STATUS_LABEL[ch.status]}</S.StatusBadge>
								{renderRowActions(ch)}
							</S.FileRow>
							{summaries[ch.path] && <S.InlineSummary>{summaries[ch.path]}</S.InlineSummary>}
						</S.FileEntry>
					))}
				</S.List>
			</>
		);
	};

	return (
		<Modal
			title={title}
			onClose={onClose}
			onCancel={onSubmit ? onClose : undefined}
			cancelLabel={onSubmit ? 'Cancel' : undefined}
			onSubmit={onSubmit}
			submitLabel={submitLabel}
			loading={loading}
			disabled={loading}
			maxWidth="520px"
		>
			{renderBody()}
		</Modal>
	);
};

export default DraftChangesModal;
